/**
 * Document Detail Page
 *
 * View and edit a document with markdown editor/preview toggle,
 * status controls (Publish/Archive/Restore), and version history.
 */

import {
  Component,
  Show,
  For,
  createSignal,
  createMemo,
  createEffect,
} from "solid-js";
import { useParams, useNavigate } from "@solidjs/router";
import { Button, Spinner, Select } from "../components/ui";
import type { SelectOption } from "../components/ui";
import { useProjectId } from "../contexts/ProjectContext";
import { useToast } from "../hooks";
import { renderMarkdown } from "../utils/markdown";
import { recordVisit } from "../stores/recentsStore";
import {
  api,
  getDocument,
  updateDocument,
  listVersions,
  getDocumentVersion,
  listLinkedWorkItems,
  linkWorkItem,
  unlinkWorkItem,
  reimportDocument,
  clearDocumentStaleness,
  type Document,
  type DocumentType,
  type DocumentStatus,
  type DocumentVersion,
  type UpdateDocumentRequest,
  type LinkedWorkItem,
  type WorkItem,
  type WorkItemListResponse,
} from "../api";
import { useProjectPrefix } from "../contexts/ProjectContext";

const DOC_TYPE_OPTIONS: SelectOption[] = [
  { value: "roadmap", label: "Roadmap" },
  { value: "architecture", label: "Architecture" },
  { value: "adr", label: "ADR" },
  { value: "design", label: "Design" },
  { value: "guide", label: "Guide" },
  { value: "custom", label: "Custom" },
];

// A doc type is a taxonomy label, not a signal: none of the six carries
// success, warning, error or AI meaning, so they all take the neutral chip.
const DOC_TYPE_CHIP = "bg-sunken text-ink-2 border-line";

const DOC_TYPE_COLORS: Record<DocumentType, string> = {
  roadmap: DOC_TYPE_CHIP,
  architecture: DOC_TYPE_CHIP,
  adr: DOC_TYPE_CHIP,
  design: DOC_TYPE_CHIP,
  guide: DOC_TYPE_CHIP,
  custom: DOC_TYPE_CHIP,
};

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  roadmap: "Roadmap",
  architecture: "Architecture",
  adr: "ADR",
  design: "Design",
  guide: "Guide",
  custom: "Custom",
};

const STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

const STATUS_COLORS: Record<DocumentStatus, string> = {
  draft: "bg-signal-amber-tint text-signal-amber",
  published: "bg-signal-green-tint text-signal-green",
  archived: "bg-sunken text-ink-3",
};

type EditorTab = "write" | "preview";
type ViewTab = "edit" | "history";

// =============================================================================
// Helper: format relative date
// =============================================================================

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins <= 1 ? "just now" : `${diffMins}m ago`;
    }
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

const VersionHistoryPanel: Component<{
  versions: DocumentVersion[];
  loading: boolean;
  selectedVersion: number | null;
  onSelectVersion: (version: number) => void;
  onRestore: (version: number) => void;
  restoring: boolean;
}> = (props) => {
  return (
    <div class="h-full flex flex-col">
      <Show when={props.loading}>
        <div class="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      </Show>

      <Show when={!props.loading && props.versions.length === 0}>
        <p class="text-sm text-ink-3 py-4 text-center">
          No previous versions yet.
        </p>
      </Show>

      <Show when={!props.loading && props.versions.length > 0}>
        <div class="flex-1 overflow-auto">
          <For each={props.versions}>
            {(ver) => (
              <button
                class={
                  // Every border-colour class lives in exactly one branch:
                  // `border-*` shorthands resolve by stylesheet order, not by
                  // class-attribute order, so the divider is named per side.
                  "w-full text-left px-4 py-3 border-b hover:bg-sunken transition-colors " +
                  (props.selectedVersion === ver.version
                    ? "bg-sunken border border-ink"
                    : "border border-transparent border-b-line")
                }
                onClick={() => props.onSelectVersion(ver.version)}
              >
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-ink">
                    v{ver.version}
                  </span>
                  <span class="text-xs text-ink-3">
                    {formatDate(ver.created_at)}
                  </span>
                </div>
                <Show when={ver.change_summary}>
                  <p class="text-xs text-ink-3 mt-1 line-clamp-2">
                    {ver.change_summary}
                  </p>
                </Show>
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};

const VersionPreview: Component<{
  version: DocumentVersion | null;
  loading: boolean;
  onRestore: () => void;
  restoring: boolean;
  onClose: () => void;
}> = (props) => {
  const renderedContent = createMemo(() => {
    const content = props.version?.content;
    if (!content) return "";
    return renderMarkdown(content);
  });

  return (
    <div class="h-full flex flex-col">
      <Show when={props.loading}>
        <div class="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      </Show>

      <Show when={props.version}>
        {(ver) => (
          <>
            <div class="flex items-center justify-between px-4 py-2 bg-sunken border-b border-line">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-ink-2">
                  v{ver().version}
                </span>
                <span class="text-xs text-ink-3">
                  {formatDate(ver().created_at)}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={props.onRestore}
                  loading={props.restoring}
                >
                  Restore this version
                </Button>
                <Button variant="secondary" size="sm" onClick={props.onClose}>
                  Close
                </Button>
              </div>
            </div>
            <div class="flex-1 overflow-auto p-4">
              <h2 class="text-lg font-semibold text-ink mb-4">{ver().title}</h2>
              <div
                class="prose prose-sm max-w-none"
                innerHTML={renderedContent()}
              />
            </div>
          </>
        )}
      </Show>
    </div>
  );
};

/**
 * Whether the section is collapsed, mirrored into `sessionStorage` so the
 * preference survives navigating between documents and a reload, and is
 * cleared with the browser session.
 */
const COLLAPSE_KEY = "flowicity_doc_linked_items_collapsed";

function readCollapsed(): boolean {
  try {
    return sessionStorage.getItem(COLLAPSE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    sessionStorage.setItem(COLLAPSE_KEY, String(collapsed));
  } catch {
    // Storage unavailable (private mode, quota) — in-memory state still works.
  }
}

const LinkedWorkItemsSection: Component<{
  projectId: string;
  documentId: string;
  prefix: string;
}> = (props) => {
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [linkedItems, setLinkedItems] = createSignal<LinkedWorkItem[]>([]);
  const [linkedLoading, setLinkedLoading] = createSignal(true);
  const [showPicker, setShowPicker] = createSignal(false);
  const [searchQuery, setSearchQuery] = createSignal("");
  const [searchResults, setSearchResults] = createSignal<WorkItem[]>([]);
  const [searchLoading, setSearchLoading] = createSignal(false);
  const [linking, setLinking] = createSignal(false);
  const [unlinkingId, setUnlinkingId] = createSignal<string | null>(null);
  const [collapsed, setCollapsed] = createSignal(readCollapsed());

  const toggleCollapsed = () => {
    const next = !collapsed();
    setCollapsed(next);
    writeCollapsed(next);
  };

  const loadLinkedItems = async () => {
    setLinkedLoading(true);
    try {
      const items = await listLinkedWorkItems(
        props.projectId,
        props.documentId,
      );
      setLinkedItems(items);
    } catch {
      showError("Failed to load linked work items");
    } finally {
      setLinkedLoading(false);
    }
  };

  createEffect(() => {
    const _pid = props.projectId;
    const _did = props.documentId;
    if (_pid && _did) {
      loadLinkedItems();
    }
  });

  let searchTimeout: ReturnType<typeof setTimeout> | undefined;
  createEffect(() => {
    const query = searchQuery();
    if (searchTimeout) clearTimeout(searchTimeout);
    if (!showPicker()) return;

    searchTimeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.get<WorkItemListResponse>(
          `/projects/${props.projectId}/work-items`,
        );
        const linkedIds = new Set(linkedItems().map((li) => li.id));
        const filtered = res.items.filter((item) => {
          if (linkedIds.has(item.id)) return false;
          if (!query) return true;
          const q = query.toLowerCase();
          const displayId = `${props.prefix}-${item.item_number}`.toLowerCase();
          return item.title.toLowerCase().includes(q) || displayId.includes(q);
        });
        setSearchResults(filtered);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  });

  const handleLink = async (workItemId: string) => {
    setLinking(true);
    try {
      await linkWorkItem(props.projectId, props.documentId, workItemId);
      await loadLinkedItems();
      setShowPicker(false);
      setSearchQuery("");
      showSuccess("Work item linked");
    } catch {
      showError("Failed to link work item");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (workItemId: string) => {
    setUnlinkingId(workItemId);
    try {
      await unlinkWorkItem(props.projectId, props.documentId, workItemId);
      setLinkedItems((prev) => prev.filter((item) => item.id !== workItemId));
      showSuccess("Work item unlinked");
    } catch {
      showError("Failed to unlink work item");
    } finally {
      setUnlinkingId(null);
    }
  };

  return (
    <div class="border-t border-line px-4 py-3 flex-shrink-0">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-medium text-ink-2">
          <button
            class="flex items-center gap-2 -ml-1 px-1 py-0.5 rounded hover:bg-sunken transition-colors"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed()}
            aria-controls="linked-work-items-panel"
            title={
              collapsed()
                ? "Expand linked work items"
                : "Collapse linked work items"
            }
          >
            <svg
              class={
                "w-4 h-4 text-ink-3 transition-transform " +
                (collapsed() ? "" : "rotate-90")
              }
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span>Linked Work Items</span>
            <Show when={linkedItems().length > 0}>
              <span class="text-xs font-normal text-ink-3">
                ({linkedItems().length})
              </span>
            </Show>
          </button>
        </h3>
        <button
          class="text-xs text-ink-2 hover:text-ink font-medium"
          onClick={() => {
            const opening = !showPicker();
            setShowPicker(opening);
            setSearchQuery("");
            if (opening && collapsed()) toggleCollapsed();
          }}
        >
          {showPicker() ? "Cancel" : "+ Link Work Item"}
        </button>
      </div>

      <div id="linked-work-items-panel">
        <Show when={!collapsed()}>
          {/* Picker */}
          <Show when={showPicker()}>
            <div class="mb-3">
              <input
                type="text"
                class="w-full px-3 py-1.5 text-sm border border-line rounded focus-ring"
                placeholder="Search work items by title or ID..."
                value={searchQuery()}
                onInput={(e) => setSearchQuery(e.currentTarget.value)}
              />
              <div class="mt-1 max-h-48 overflow-auto border border-line rounded bg-surface">
                <Show when={searchLoading()}>
                  <div class="flex justify-center py-3">
                    <Spinner size="sm" />
                  </div>
                </Show>
                <Show when={!searchLoading() && searchResults().length === 0}>
                  <p class="text-xs text-ink-3 py-3 text-center">
                    {searchQuery()
                      ? "No matching work items"
                      : "No available work items"}
                  </p>
                </Show>
                <Show when={!searchLoading() && searchResults().length > 0}>
                  <For each={searchResults()}>
                    {(item) => (
                      <button
                        class="w-full text-left px-3 py-2 text-sm hover:bg-sunken transition-colors border-b border-line last:border-b-0 flex items-center gap-2 disabled:opacity-50"
                        onClick={() => handleLink(item.id)}
                        disabled={linking()}
                      >
                        <span class="text-xs font-mono text-ink-3 flex-shrink-0">
                          {props.prefix}-{item.item_number}
                        </span>
                        <span class="text-ink-2 truncate">{item.title}</span>
                      </button>
                    )}
                  </For>
                </Show>
              </div>
            </div>
          </Show>

          {/* Linked items list */}
          <Show when={linkedLoading()}>
            <div class="flex justify-center py-2">
              <Spinner size="sm" />
            </div>
          </Show>
          <Show
            when={
              !linkedLoading() && linkedItems().length === 0 && !showPicker()
            }
          >
            <p class="text-xs text-ink-3 py-1">No linked work items.</p>
          </Show>
          <Show when={!linkedLoading() && linkedItems().length > 0}>
            <div class="space-y-1">
              <For each={linkedItems()}>
                {(item) => (
                  <div class="flex items-center gap-2 group py-1">
                    <button
                      class="flex items-center gap-2 text-sm text-ink-2 hover:text-ink transition-colors min-w-0 flex-1"
                      onClick={() => navigate(`/items/${item.id}`)}
                    >
                      <span class="text-xs font-mono text-ink-3 flex-shrink-0">
                        {props.prefix}-{item.item_number}
                      </span>
                      <span class="truncate">{item.title}</span>
                    </button>
                    <button
                      class="opacity-0 group-hover:opacity-100 text-ink-3 hover:text-signal-red transition-all flex-shrink-0 p-0.5"
                      onClick={() => handleUnlink(item.id)}
                      disabled={unlinkingId() === item.id}
                      title="Remove link"
                    >
                      <Show
                        when={unlinkingId() !== item.id}
                        fallback={<Spinner size="sm" />}
                      >
                        <svg
                          class="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </Show>
                    </button>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </div>
    </div>
  );
};

export const DocumentDetailPage: Component = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = useProjectId();
  const prefix = useProjectPrefix();
  const { showError, showSuccess } = useToast();

  const [doc, setDoc] = createSignal<Document | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [loadError, setLoadError] = createSignal(false);

  const [title, setTitle] = createSignal("");
  const [content, setContent] = createSignal("");
  const [docType, setDocType] = createSignal<DocumentType>("custom");
  const [editorTab, setEditorTab] = createSignal<EditorTab>("write");
  const [changeSummary, setChangeSummary] = createSignal("");

  const [saving, setSaving] = createSignal(false);
  const [statusUpdating, setStatusUpdating] = createSignal(false);
  const [reimporting, setReimporting] = createSignal(false);
  const [dismissingStaleness, setDismissingStaleness] = createSignal(false);

  const [viewTab, setViewTab] = createSignal<ViewTab>("edit");

  const [versions, setVersions] = createSignal<DocumentVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = createSignal(false);
  const [selectedVersion, setSelectedVersion] = createSignal<number | null>(
    null,
  );
  const [versionDetail, setVersionDetail] =
    createSignal<DocumentVersion | null>(null);
  const [versionDetailLoading, setVersionDetailLoading] = createSignal(false);
  const [restoring, setRestoring] = createSignal(false);

  const hasChanges = createMemo(() => {
    const d = doc();
    if (!d) return false;
    return (
      title() !== d.title ||
      content() !== (d.content ?? "") ||
      docType() !== d.doc_type
    );
  });

  const renderedContent = createMemo(() => {
    const md = content();
    if (!md) return "<p class='text-ink-3'>Nothing to preview</p>";
    return renderMarkdown(md);
  });

  const loadDocument = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const document = await getDocument(projectId(), params.id);
      setDoc(document);
      if (projectId()) recordVisit("document", document.id, projectId());
      setTitle(document.title);
      setContent(document.content ?? "");
      setDocType(document.doc_type);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    setVersionsLoading(true);
    try {
      const res = await listVersions(projectId(), params.id);
      setVersions(res.items);
    } catch {
      showError("Failed to load version history");
    } finally {
      setVersionsLoading(false);
    }
  };

  const loadVersionDetail = async (version: number) => {
    setSelectedVersion(version);
    setVersionDetailLoading(true);
    try {
      const ver = await getDocumentVersion(projectId(), params.id, version);
      setVersionDetail(ver);
    } catch {
      showError("Failed to load version");
    } finally {
      setVersionDetailLoading(false);
    }
  };

  const handleSave = async () => {
    const d = doc();
    if (!d) return;

    setSaving(true);
    try {
      const req: UpdateDocumentRequest = {};
      if (title() !== d.title) req.title = title();
      if (content() !== (d.content ?? "")) req.content = content();
      if (docType() !== d.doc_type) req.doc_type = docType();
      const summary = changeSummary().trim();
      if (summary) req.change_summary = summary;

      const updated = await updateDocument(projectId(), d.id, req);
      setDoc(updated);
      setTitle(updated.title);
      setContent(updated.content ?? "");
      setDocType(updated.doc_type);
      setChangeSummary("");
      showSuccess("Document saved");
    } catch {
      showError("Failed to save document");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: DocumentStatus) => {
    const d = doc();
    if (!d) return;

    setStatusUpdating(true);
    try {
      const updated = await updateDocument(projectId(), d.id, {
        status: newStatus,
        change_summary:
          newStatus === "published"
            ? "Published document"
            : newStatus === "archived"
              ? "Archived document"
              : "Restored document",
      });
      setDoc(updated);
      showSuccess(
        newStatus === "published"
          ? "Document published"
          : newStatus === "archived"
            ? "Document archived"
            : "Document restored to draft",
      );
    } catch {
      showError("Failed to update status");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleReimport = async () => {
    const d = doc();
    if (!d) return;
    setReimporting(true);
    try {
      await reimportDocument(projectId(), d.id);
      const refreshed = await getDocument(projectId(), d.id);
      setDoc(refreshed);
      setTitle(refreshed.title);
      setContent(refreshed.content ?? "");
      setDocType(refreshed.doc_type);
      showSuccess("Document re-imported from GitHub");
    } catch {
      showError("Failed to re-import document");
    } finally {
      setReimporting(false);
    }
  };

  const handleDismissStaleness = async () => {
    const d = doc();
    if (!d) return;
    setDismissingStaleness(true);
    try {
      const updated = await clearDocumentStaleness(projectId(), d.id);
      setDoc(updated);
      showSuccess("Staleness dismissed");
    } catch {
      showError("Failed to dismiss staleness");
    } finally {
      setDismissingStaleness(false);
    }
  };

  const handleRestoreVersion = async () => {
    const ver = versionDetail();
    const d = doc();
    if (!ver || !d) return;

    setRestoring(true);
    try {
      const updated = await updateDocument(projectId(), d.id, {
        title: ver.title,
        content: ver.content ?? "",
        change_summary: `Restored from v${ver.version}`,
      });
      setDoc(updated);
      setTitle(updated.title);
      setContent(updated.content ?? "");
      setDocType(updated.doc_type);
      setSelectedVersion(null);
      setVersionDetail(null);
      setViewTab("edit");
      showSuccess(`Restored to v${ver.version}`);
    } catch {
      showError("Failed to restore version");
    } finally {
      setRestoring(false);
    }
  };

  createEffect(() => {
    // Re-run when projectId or params.id change
    const _pid = projectId();
    const _id = params.id;
    if (_pid && _id) {
      loadDocument();
    }
  });

  createEffect(() => {
    if (viewTab() === "history") {
      loadVersions();
    }
  });

  return (
    <div class="h-full flex flex-col overflow-hidden">
      {/* Loading */}
      <Show when={loading()}>
        <div class="flex justify-center items-center flex-1">
          <Spinner size="lg" />
        </div>
      </Show>

      {/* Error */}
      <Show when={loadError()}>
        <div class="flex flex-col items-center justify-center flex-1 gap-4">
          <p class="text-sm text-signal-red">Failed to load document.</p>
          <div class="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate("/documents")}
            >
              Back to Documents
            </Button>
            <Button variant="primary" size="sm" onClick={loadDocument}>
              Try again
            </Button>
          </div>
        </div>
      </Show>

      {/* Document loaded */}
      <Show when={!loading() && !loadError() && doc()}>
        {(currentDoc) => (
          <>
            {/* Top bar */}
            <div class="flex items-center gap-3 px-4 py-3 border-b border-line bg-surface flex-shrink-0">
              <button
                class="text-ink-3 hover:text-ink transition-colors"
                onClick={() => navigate("/documents")}
                title="Back to documents"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Status badge */}
              <span
                class={
                  "px-2 py-0.5 text-xs font-medium rounded-full " +
                  STATUS_COLORS[currentDoc().status]
                }
              >
                {STATUS_LABELS[currentDoc().status]}
              </span>

              {/* Doc type badge */}
              <span
                class={
                  "inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border " +
                  DOC_TYPE_COLORS[currentDoc().doc_type]
                }
              >
                {DOC_TYPE_LABELS[currentDoc().doc_type]}
              </span>

              {/* Version */}
              <span class="text-xs text-ink-3">v{currentDoc().version}</span>

              {/* Last updated */}
              <span class="text-xs text-ink-3">
                Updated {formatDate(currentDoc().updated_at)}
              </span>

              <div class="flex-1" />

              {/* Status action buttons */}
              <Show when={currentDoc().status === "draft"}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleStatusChange("published")}
                  loading={statusUpdating()}
                >
                  Publish
                </Button>
              </Show>
              <Show when={currentDoc().status === "published"}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleStatusChange("archived")}
                  loading={statusUpdating()}
                >
                  Archive
                </Button>
              </Show>
              <Show when={currentDoc().status === "archived"}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleStatusChange("draft")}
                  loading={statusUpdating()}
                >
                  Restore to Draft
                </Button>
              </Show>
            </div>

            {/* Stale banner */}
            <Show when={currentDoc().is_stale}>
              <div class="flex items-center gap-2 px-4 py-2 bg-signal-amber-tint border-b border-signal-amber/25 text-sm text-signal-amber flex-shrink-0">
                <svg
                  class="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <span class="flex-1">
                  This document was updated in GitHub
                  <Show when={currentDoc().stale_since}>
                    {" "}
                    on {formatDate(currentDoc().stale_since as string)}
                  </Show>
                  .
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleReimport}
                  loading={reimporting()}
                >
                  Re-import
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDismissStaleness}
                  loading={dismissingStaleness()}
                >
                  Dismiss
                </Button>
              </div>
            </Show>

            {/* View tabs: Edit / History */}
            <div class="flex items-center border-b border-line bg-surface px-4 flex-shrink-0">
              <button
                class={
                  "px-3 py-2 text-sm font-medium border-b-2 transition-colors " +
                  (viewTab() === "edit"
                    ? "border-ink text-ink"
                    : "border-transparent text-ink-3 hover:text-ink")
                }
                onClick={() => {
                  setViewTab("edit");
                  setSelectedVersion(null);
                  setVersionDetail(null);
                }}
              >
                Edit
              </button>
              <button
                class={
                  "px-3 py-2 text-sm font-medium border-b-2 transition-colors " +
                  (viewTab() === "history"
                    ? "border-ink text-ink"
                    : "border-transparent text-ink-3 hover:text-ink")
                }
                onClick={() => setViewTab("history")}
              >
                History
              </button>
            </div>

            {/* Edit view */}
            <Show when={viewTab() === "edit"}>
              <div class="flex-1 flex flex-col overflow-hidden">
                {/* Title + type */}
                <div class="px-4 pt-4 pb-2 flex-shrink-0">
                  <input
                    type="text"
                    class="w-full text-xl font-semibold text-ink border-0 border-b border-transparent focus:border-line-strong focus:outline-none bg-transparent pb-1 transition-colors"
                    value={title()}
                    onInput={(e) => setTitle(e.currentTarget.value)}
                    placeholder="Document title..."
                  />
                  <div class="flex items-center gap-3 mt-2">
                    <div class="w-40">
                      <Select
                        value={docType()}
                        onChange={(v) => setDocType(v as DocumentType)}
                        options={DOC_TYPE_OPTIONS}
                      />
                    </div>
                  </div>
                </div>

                {/* Editor tabs: Write / Preview */}
                <div class="flex items-center gap-1 px-4 pt-2 pb-1 flex-shrink-0">
                  <button
                    class={
                      "px-3 py-1.5 text-xs font-medium rounded-md transition-colors " +
                      (editorTab() === "write"
                        ? "bg-ink text-paper"
                        : "text-ink-2 hover:bg-sunken")
                    }
                    onClick={() => setEditorTab("write")}
                  >
                    Write
                  </button>
                  <button
                    class={
                      "px-3 py-1.5 text-xs font-medium rounded-md transition-colors " +
                      (editorTab() === "preview"
                        ? "bg-ink text-paper"
                        : "text-ink-2 hover:bg-sunken")
                    }
                    onClick={() => setEditorTab("preview")}
                  >
                    Preview
                  </button>
                </div>

                {/* Editor / Preview area */}
                <div class="flex-1 px-4 pb-2 overflow-hidden">
                  <Show when={editorTab() === "write"}>
                    <textarea
                      class="w-full h-full resize-none border border-line rounded p-3 text-sm text-ink font-mono focus-ring"
                      value={content()}
                      onInput={(e) => setContent(e.currentTarget.value)}
                      placeholder="Write your document in markdown..."
                    />
                  </Show>
                  <Show when={editorTab() === "preview"}>
                    <div class="w-full h-full overflow-auto border border-line rounded p-4">
                      <div
                        class="prose prose-sm max-w-none"
                        innerHTML={renderedContent()}
                      />
                    </div>
                  </Show>
                </div>

                {/* Linked Work Items */}
                <LinkedWorkItemsSection
                  projectId={projectId()}
                  documentId={params.id}
                  prefix={prefix()}
                />

                {/* Save bar */}
                <div class="flex items-center gap-3 px-4 py-3 border-t border-line bg-surface flex-shrink-0">
                  <input
                    type="text"
                    class="flex-1 px-3 py-1.5 text-sm border border-line rounded focus-ring"
                    placeholder="Change summary (optional)..."
                    value={changeSummary()}
                    onInput={(e) => setChangeSummary(e.currentTarget.value)}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    loading={saving()}
                    disabled={!hasChanges()}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Show>

            {/* History view */}
            <Show when={viewTab() === "history"}>
              <div class="flex-1 flex overflow-hidden">
                {/* Version list */}
                <div class="w-64 border-r border-line overflow-auto flex-shrink-0">
                  <VersionHistoryPanel
                    versions={versions()}
                    loading={versionsLoading()}
                    selectedVersion={selectedVersion()}
                    onSelectVersion={loadVersionDetail}
                    onRestore={handleRestoreVersion}
                    restoring={restoring()}
                  />
                </div>

                {/* Version preview */}
                <div class="flex-1 overflow-hidden">
                  <Show
                    when={selectedVersion() !== null}
                    fallback={
                      <div class="flex items-center justify-center h-full text-sm text-ink-3">
                        Select a version to preview
                      </div>
                    }
                  >
                    <VersionPreview
                      version={versionDetail()}
                      loading={versionDetailLoading()}
                      onRestore={handleRestoreVersion}
                      restoring={restoring()}
                      onClose={() => {
                        setSelectedVersion(null);
                        setVersionDetail(null);
                      }}
                    />
                  </Show>
                </div>
              </div>
            </Show>
          </>
        )}
      </Show>
    </div>
  );
};
